import React from "react"
import styled from "styled-components"
import { Link } from "gatsby"

import Layout from "../components/layout"
import SEO from "../components/seo"

const Container = styled.section`
  padding: 100px var(--gutter-s) 50px;
  @media (min-width: 768px) {
    padding: 150px var(--gutter-l) 50px;
  }
`

const HeadingContainer = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--manatee);

  h1 {
    margin-bottom: 0.25rem;
  }
`

const Block = styled.div`
  margin-bottom: 2rem;
  h4,
  h5,
  p,
  li {
    color: var(--manatee);
    line-height: 150%;
  }
  h4 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  h5 {
    font-size: 0.95rem;
    margin: 1rem 0 0.5rem;
  }
  p {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
  ol {
    li {
      list-style: upper-alpha;
      font-size: 0.9rem;
      margin-bottom: 0.5rem;
    }
  }
  a {
    color: var(--havelock-blue);
    cursor: pointer;
  }
`

const Terms = () => {
  return (
    <Layout metaNavbar={true}>
      <SEO
        title="Terms"
        keywords={[
          `submitt`,
          `submitt app`,
          `medical app`,
          "medical claims",
          "doctors",
        ]}
      />
      <Container>
        <HeadingContainer>
          <h1>Terms and Conditions of Use</h1>
          <h5>Version: 1.1</h5>
          <h5>Effective Date: 16 September 2026</h5>
        </HeadingContainer>

        <Block>
          <ol>
            <li>
              These Terms and Conditions of Use (“Terms”) govern your access to
              and use of Submitt.App (“Submitt”, the “Application” or the
              “Services”), operated by Submitt Medical Accounts (Pty) Ltd,
              registration number 2013/008088/07 (“Submitt Medical Accounts”,
              “we”, “us” or “our”).
            </li>
            <li>
              By creating an account, accessing or using the Application, or
              accepting these Terms electronically, you agree to be bound by
              these Terms.
            </li>
            <li>
              If you access or use the Application on behalf of a medical
              practice, medical bureau or other organisation, you represent and
              warrant that you are authorised to accept these Terms on its
              behalf, in which event “you” and “your” include that organisation.
            </li>
            <li>
              These Terms must be read together with our{" "}
              <Link to="/privacy/">POPIA Privacy Policy</Link>, which forms part
              of the contractual framework governing the use of the Application.
            </li>
          </ol>
        </Block>

        <Block>
          <h4>1. DEFINITIONS AND INTERPRETATION</h4>
          <p>In these Terms, unless the context indicates otherwise:</p>
          <p>
            1.1. “Account” means the account registered by or on behalf of a
            Customer for access to and use of the Application.
          </p>
          <p>
            1.2. “Application” means the Submitt.App software application,
            platform and associated functionality operated and made available by
            Submitt Medical Accounts (Pty) Ltd.
          </p>
          <p>
            1.3. “Authorised User” means any individual authorised by the
            Customer to access or use the Application on the Customer’s behalf,
            including a medical practitioner, employee, assistant, medical
            billing bureau or other authorised representative.
          </p>
          <p>
            1.4. “Customer” means the medical practice, medical practitioner or
            other entity in whose name an Account is registered and which
            subscribes to, accesses or uses the Services, whether or not the
            subscription fee in respect of that Account is paid by a medical
            billing bureau or other third party.
          </p>
          <p>
            1.5. “Customer Data” means any information, documents, images,
            records, patient information, claim information or other data
            submitted to, uploaded to, generated through or otherwise processed
            by the Application by or on behalf of the Customer or an Authorised
            User.
          </p>
          <p>
            1.6. “GoodX” means the third-party medical billing and practice
            management system with which the Application may integrate or
            communicate through an application programming interface or other
            integration.
          </p>
          <p>
            1.7. “Personal Information” has the meaning ascribed to it in the
            Protection of Personal Information Act 4 of 2013 (“POPIA”).
          </p>
          <p>
            1.8. “Patient Information” means Personal Information relating to a
            patient which is submitted to, captured by, uploaded to or otherwise
            processed through the Application.
          </p>
          <p>
            1.9. “Privacy Policy” means Submitt’s POPIA Privacy Policy
            applicable to the Application and the Services, as amended from time
            to time.
          </p>
          <p>
            1.10. “Services” means the software, functionality, features and
            related services made available by Submitt through or in connection
            with the Application.
          </p>
          <p>
            1.11. “Submitt”, “we”, “us” or “our” means Submitt Medical Accounts
            (Pty) Ltd, registration number 2013/008088/07.
          </p>
          <p>
            1.12. “Terms” means these Terms and Conditions of Use, together with
            any documents expressly incorporated into them.
          </p>
          <p>
            1.13. “User” means any person who accesses or uses the Application,
            including an Authorised User.
          </p>
          <h5>1.14. Interpretation</h5>
          <p>Unless the context indicates otherwise:</p>
          <p>
            1.14.1. words importing the singular include the plural and vice
            versa;
          </p>
          <p>1.14.2. words importing any gender include the other genders;</p>
          <p>
            1.14.3. a reference to a person includes a natural person, juristic
            person, partnership, association or other legal entity;
          </p>
          <p>
            1.14.4. a reference to legislation includes that legislation as
            amended, consolidated or replaced from time to time;
          </p>
          <p>
            1.14.5. headings are for convenience only and do not affect the
            interpretation of these Terms; and
          </p>
          <p>
            1.14.6. where a word or expression is defined, other grammatical
            forms of that word or expression have corresponding meanings.
          </p>
        </Block>

        <Block>
          <h4>2. THE SUBMITT SERVICE</h4>
          <h5>2.1. Nature of the Application</h5>
          <p>
            Submitt.App is a software and billing-administration application
            operated by Submitt Medical Accounts (Pty) Ltd and made available to
            medical practitioners, medical practices and medical billing bureaux
            to assist with the administration and preparation of medical claims.
          </p>
          <p>
            Submitt is not a healthcare provider, medical practitioner or
            medical billing bureau and does not provide medical, clinical or
            healthcare services.
          </p>
          <p>
            Submitt does not diagnose, treat or advise patients, does not
            provide medical or clinical recommendations, and does not establish
            or assume a professional relationship with any patient. The
            relationship relating to the provision of healthcare services
            remains between the relevant healthcare practitioner and the
            patient.
          </p>
          <h5>2.2. Functionality</h5>
          <p>
            The Application provides functionality intended to assist the
            Customer with the administrative preparation and management of
            medical claims. Depending on the functionality made available to the
            Customer, the Application may facilitate the capture and processing
            of patient and claim information, provide billing, ICD-10 and macro
            codes, and facilitate the transmission of claims or claims-related
            information to the Customer’s nominated medical billing bureau or
            other third-party service provider.
          </p>
          <p>
            The Customer acknowledges that the Application is an administrative
            tool and does not replace the professional judgement, review or
            decision-making of the Customer, its healthcare practitioners or its
            medical billing bureau.
          </p>
          <h5>2.3. Medical Claims</h5>
          <p>
            Submitt does not itself provide medical services, determine whether
            a medical service should be provided, determine whether a claim is
            medically or professionally appropriate, or guarantee that any claim
            will be accepted or paid by a medical scheme or other third party.
          </p>
          <p>
            The Customer remains responsible for ensuring that all claims and
            claims-related information generated, processed or transmitted
            through the Application are accurate, complete and properly
            authorised before submission.
          </p>
          <h5>2.4. AI-Assisted Information Extraction</h5>
          <p>
            The Application uses Amazon Textract, an artificial intelligence
            service provided through Amazon Web Services (“AWS”), to assist with
            the extraction of specified information from images uploaded to the
            Application.
          </p>
          <p>
            The information extracted may include the medical aid name, medical
            aid membership number and identification number appearing on a
            patient’s hospital or medical aid documentation.
          </p>
          <p>
            The Customer acknowledges that automated information extraction may
            produce inaccurate, incomplete or incorrectly interpreted
            information. The Customer is accordingly responsible for reviewing
            and verifying information extracted through the Application before
            relying on such information for coding, billing or claim submission.
          </p>
          <p>
            Submitt does not warrant or represent that information extracted
            through Amazon Textract will be accurate, complete or error-free.
          </p>
          <h5>2.5. Human Verification</h5>
          <p>
            The Customer acknowledges that the AI-assisted functionality is
            intended to assist with the administrative process and does not
            replace human verification.
          </p>
          <p>
            The Customer must ensure that an appropriately authorised person
            verifies the relevant patient and medical aid information before any
            coding, billing or claim is submitted or otherwise relied upon.
          </p>
          <h5>2.6. Third-Party Systems and Services</h5>
          <p>
            The operation of certain functionality within the Application may
            depend upon third-party systems, services, infrastructure or
            integrations, including AWS, Amazon Textract, GoodX and the systems
            of medical billing bureaux.
          </p>
          <p>
            Submitt does not control the operation of third-party systems and
            does not warrant their continued availability, functionality or
            compatibility with the Application.
          </p>
          <p>
            The Customer acknowledges that changes, interruptions or failures
            affecting a third-party system may affect the corresponding
            functionality of the Application.
          </p>
        </Block>

        <Block>
          <h4>3. ACCOUNTS AND AUTHORISED USERS</h4>
          <h5>3.1. Account Registration</h5>
          <p>
            Access to the Application requires the Customer to register an
            Account with Submitt. The Customer must ensure that all information
            provided to Submitt in connection with the registration,
            establishment and maintenance of its Account is accurate, complete
            and kept up to date.
          </p>
          <p>
            The Customer warrants that the person registering the Account is
            duly authorised to do so on behalf of the Customer.
          </p>
          <h5>3.2. Authorised Users</h5>
          <p>
            The Customer may permit its employees, medical practitioners,
            assistants, medical billing bureau personnel and other appropriately
            authorised persons to access and use the Application as Authorised
            Users, subject to these Terms.
          </p>
          <p>The Customer is responsible for:</p>
          <p>
            3.2.1. determining which persons are authorised to access the
            Application;
          </p>
          <p>
            3.2.2. ensuring that each Authorised User accesses and uses the
            Application only for legitimate purposes connected with the
            Customer’s use of the Services;
          </p>
          <p>
            3.2.3. ensuring that each Authorised User complies with these Terms
            and all applicable confidentiality, privacy, security and
            professional obligations;
          </p>
          <p>
            3.2.4. ensuring that access rights are promptly withdrawn when an
            Authorised User no longer requires access to the Application; and
          </p>
          <p>
            3.2.5. all acts and omissions of its Authorised Users in connection
            with the Application.
          </p>
          <h5>3.3. Login Credentials</h5>
          <p>
            Each Authorised User must use their own login credentials. Login
            credentials may not be shared, transferred or made available to any
            other person.
          </p>
          <p>
            The Customer and each Authorised User are responsible for
            maintaining the confidentiality and security of their login
            credentials and must take reasonable measures to prevent
            unauthorised access to the Account.
          </p>
          <h5>3.4. Unauthorised Access</h5>
          <p>
            The Customer must immediately notify Submitt if it becomes aware of
            or reasonably suspects that:
          </p>
          <p>
            3.4.1. an unauthorised person has obtained access to the Account;
          </p>
          <p>
            3.4.2. an Authorised User’s login credentials have been compromised;
            or
          </p>
          <p>
            3.4.3. the Application or Customer Data has been accessed or used
            without authorisation.
          </p>
          <p>
            The Customer must cooperate reasonably with Submitt in investigating
            and addressing any suspected unauthorised access or security
            incident.
          </p>
          <h5>3.5. Responsibility for Account Activity</h5>
          <p>
            Subject to Submitt’s obligations under these Terms, the Customer
            remains responsible for activity undertaken through its Account and
            by its Authorised Users.
          </p>
          <p>
            The Customer may not permit any person to access or use the
            Application who is not appropriately authorised to do so.
          </p>
          <h5>3.6. Activity Under Account Credentials</h5>
          <p>
            3.6.1. All access to and use of the Application effected through the
            Customer’s Account or login credentials, whether by an Authorised
            User or by any other person, and whether or not authorised by the
            Customer, is deemed for purposes of these Terms to be the act of the
            Customer.
          </p>
          <p>
            3.6.2. Clause 3.6.1 applies whether or not the Customer permitted,
            was aware of, or could reasonably have prevented the access
            concerned, and the Customer indemnifies Submitt in respect of it in
            accordance with clause 13.8.
          </p>
          <p>
            3.6.3. Clause 3.6.1 does not apply to access obtained as a direct
            result of a breach by Submitt of its obligations under clauses 5.7
            and 10.1.
          </p>
          <p>
            3.6.4. Nothing in this clause derogates from the Customer’s
            obligations under clause 3.3, or limits Submitt’s rights under
            clause 14.
          </p>
          <h5>3.7. Suspension of User Access</h5>
          <p>
            Submitt may suspend or restrict an individual Authorised User’s
            access where Submitt reasonably believes that the User has breached
            these Terms, poses a security or privacy risk, or is using the
            Application in an unauthorised or unlawful manner.
          </p>
          <p>
            Where reasonably practicable, Submitt will notify the Customer of
            such suspension or restriction.
          </p>
        </Block>

        <Block>
          <h4>4. CUSTOMER RESPONSIBILITIES</h4>
          <h5>4.1. Responsibility for Use of the Application</h5>
          <p>
            The Customer acknowledges that Submitt.App is a software and
            billing-administration tool and that the Customer remains
            responsible for the manner in which it uses the Application and the
            Services.
          </p>
          <p>
            The Customer is responsible for ensuring that its use of the
            Application is lawful, appropriate and consistent with the
            Customer’s professional, regulatory and business obligations.
          </p>
          <h5>4.2. Patient Information</h5>
          <p>
            The Customer is responsible for ensuring that all Patient
            Information submitted to or processed through the Application has
            been lawfully collected and that the Customer is entitled to provide
            such information to Submitt for processing.
          </p>
          <p>
            The Customer remains responsible for obtaining any consent,
            authorisation or other lawful basis required for the processing of
            Patient Information and for providing patients with any notices
            required by applicable law.
          </p>
          <h5>4.3. Accuracy of Information</h5>
          <p>
            The Customer is responsible for the accuracy, completeness and
            correctness of information entered into or submitted through the
            Application.
          </p>
          <p>
            Where information is extracted through the Application’s AI-assisted
            functionality, the Customer is responsible for ensuring that such
            information is appropriately reviewed and verified before it is
            relied upon.
          </p>
          <p>
            Submitt does not assume responsibility for errors or omissions in
            information supplied by the Customer or generated through automated
            processing.
          </p>
          <h5>4.4. Claims and Billing</h5>
          <p>
            The Customer remains responsible for the preparation, review and
            accuracy of its medical claims and billing information.
          </p>
          <p>
            The Customer must ensure that all claims are appropriately reviewed
            and authorised before being transmitted to its medical billing
            bureau, medical scheme or any other third party.
          </p>
          <p>
            The Customer acknowledges that Submitt does not determine whether a
            claim is medically, professionally or factually appropriate, nor
            whether a claim will be accepted or paid by a medical scheme.
          </p>
          <h5>4.5. Professional Responsibility</h5>
          <p>
            Nothing in these Terms transfers any professional responsibility of
            a medical practitioner, healthcare provider or medical billing
            professional to Submitt.
          </p>
          <p>
            The Customer and its relevant professional users remain responsible
            for complying with all professional rules, ethical obligations and
            regulatory requirements applicable to their activities.
          </p>
          <h5>4.6. Staff and User Access</h5>
          <p>
            The Customer is responsible for ensuring that its employees,
            practitioners, assistants, contractors and other Authorised Users:
          </p>
          <p>4.6.1. are appropriately authorised to access the Application;</p>
          <p>4.6.2. use the Application only for legitimate purposes;</p>
          <p>4.6.3. comply with these Terms and applicable law;</p>
          <p>
            4.6.4. maintain the confidentiality and security of their login
            credentials; and
          </p>
          <p>
            4.6.5. comply with all applicable confidentiality, privacy and
            information-security obligations.
          </p>
          <p>
            The Customer remains responsible for the acts and omissions of its
            Authorised Users in connection with their use of the Application.
          </p>
          <h5>4.7. Record-Keeping</h5>
          <p>
            The Customer remains responsible for maintaining all patient,
            clinical, billing and other records which it is required to retain
            in terms of applicable law, professional rules or its own policies.
          </p>
          <p>
            The Customer acknowledges that use of the Application does not
            relieve it of any statutory, regulatory or professional
            record-keeping obligation.
          </p>
          <h5>4.8. Security</h5>
          <p>
            The Customer is responsible for maintaining appropriate security
            measures in respect of its own devices, systems, networks, Account
            credentials and access to the Application.
          </p>
          <p>
            The Customer must notify Submitt without undue delay if it becomes
            aware of any unauthorised access to its Account or any suspected
            compromise of Customer Data or Patient Information.
          </p>
          <h5>4.9. Prohibited Conduct</h5>
          <p>The Customer must not use the Application:</p>
          <p>4.9.1. for any unlawful or unauthorised purpose;</p>
          <p>
            4.9.2. to process information which the Customer is not lawfully
            entitled to process;
          </p>
          <p>
            4.9.3. to obtain or attempt to obtain unauthorised access to the
            Application or any underlying system;
          </p>
          <p>
            4.9.4. in a manner that could compromise the security, integrity or
            availability of the Application;
          </p>
          <p>
            4.9.5. to transmit malicious code, viruses or other harmful
            material; or
          </p>
          <p>
            4.9.6. in any manner which could reasonably be expected to expose
            Submitt or any third party to unlawful, fraudulent or abusive
            conduct.
          </p>
          <h5>4.10. Disclosure of Bureau and Third-Party Access</h5>
          <p>
            The Customer must disclose to Submitt, on registration and
            thereafter on request or whenever it changes, the identity of any
            medical billing bureau or other third party to whom the Customer
            gives access to its Account or to the Application.
          </p>
          <p>
            The Customer must not permit access to its Account or to the
            Application by any medical billing bureau or other third party whose
            identity has not been disclosed to Submitt in accordance with this
            clause. Permitting undisclosed access is a material breach of these
            Terms.
          </p>
        </Block>

        <Block>
          <h4>5. PATIENT INFORMATION AND POPIA</h4>
          <h5>5.1. Responsibility for Patient Information</h5>
          <p>
            The Customer acknowledges that, in using the Application to collect,
            capture, upload, store or otherwise process Patient Information, the
            Customer remains responsible for determining the purpose and means
            of processing such information and for complying with its
            obligations under the Protection of Personal Information Act 4 of
            2013 (“POPIA”).
          </p>
          <p>
            The Customer accordingly remains responsible for ensuring that
            Patient Information is lawfully collected and processed and that it
            has obtained any consent, authorisation or other lawful basis
            required for such processing.
          </p>
          <h5>5.2. Submitt’s Role as Operator</h5>
          <p>
            To the extent that Submitt processes Patient Information on behalf
            of the Customer in providing the Services, Submitt acts as an
            operator on behalf of the Customer.
          </p>
          <p>
            Submitt will process Patient Information only to the extent
            reasonably necessary to provide, maintain, secure and support the
            Services, in accordance with the Customer’s lawful instructions,
            these Terms and applicable law.
          </p>
          <h5>5.3. Customer’s Instructions</h5>
          <p>
            The Customer authorises Submitt to process Patient Information to
            the extent reasonably necessary to:
          </p>
          <p>5.3.1. provide and operate the Application;</p>
          <p>
            5.3.2. capture, store and process Patient Information submitted by
            the Customer or its Authorised Users;
          </p>
          <p>
            5.3.3. provide the Application’s billing, coding and administrative
            functionality;
          </p>
          <p>
            5.3.4. process information through the Application’s AI-assisted
            functionality, including Amazon Textract;
          </p>
          <p>
            5.3.5. facilitate the transmission of claims or claims-related
            information to the Customer’s nominated medical billing bureau or
            other authorised third-party service provider;
          </p>
          <p>
            5.3.6. maintain, secure, troubleshoot and support the Application;
          </p>
          <p>
            5.3.7. perform backups, recovery and other reasonable operational
            functions; and
          </p>
          <p>
            5.3.8. comply with any legal or regulatory obligation applicable to
            Submitt.
          </p>
          <h5>5.4. Special Personal Information</h5>
          <p>
            The Customer acknowledges that Patient Information processed through
            the Application may constitute special personal information,
            including information concerning a person’s health or medical
            information, as contemplated by POPIA.
          </p>
          <p>
            The Customer is responsible for ensuring that the processing of such
            information through the Application is permitted under POPIA and
            that all applicable requirements relating to special personal
            information are satisfied.
          </p>
          <h5>5.5. Third-Party Processing</h5>
          <p>
            The Customer acknowledges that the provision of the Services may
            require Patient Information to be processed by third-party service
            providers engaged by Submitt.
          </p>
          <p>
            These service providers may include cloud infrastructure and
            technology providers, including Amazon Web Services (“AWS”) and
            Amazon Textract, as further described in Submitt’s Privacy Policy.
          </p>
          <p>
            The Customer authorises Submitt to use such service providers to the
            extent reasonably necessary to provide the Services, subject to
            Submitt’s obligations under applicable law and the Privacy Policy.
          </p>
          <h5>5.6. Cross-Border Processing</h5>
          <p>
            The Customer acknowledges that certain Patient Information may be
            processed or stored outside the Republic of South Africa in
            connection with the provision of the Services, including through AWS
            infrastructure located in the European Union.
          </p>
          <p>
            Submitt will address such processing in accordance with applicable
            requirements under POPIA, and further information regarding the
            relevant hosting and processing arrangements is set out in the
            Privacy Policy.
          </p>
          <h5>5.7. Security and Confidentiality</h5>
          <p>
            Submitt will implement reasonable technical and organisational
            measures appropriate to the nature of the Patient Information
            processed through the Application to protect such information
            against unauthorised access, loss, damage or disclosure.
          </p>
          <p>
            The Customer acknowledges that the Customer remains responsible for
            security measures within its own environment, including the security
            of devices, networks, user credentials and access granted to
            Authorised Users.
          </p>
          <h5>5.8. Data Subject Requests</h5>
          <p>
            Where a patient or other data subject makes a request relating to
            Patient Information processed through the Application, the Customer
            remains responsible for dealing with the request as the party
            responsible for the relevant processing.
          </p>
          <p>
            Where reasonably necessary, Submitt will provide reasonable
            assistance to the Customer in relation to such a request to the
            extent that the relevant information is held by Submitt and Submitt
            is legally permitted to provide such assistance.
          </p>
          <h5>5.9. Retention and Deletion</h5>
          <p>
            Patient Information will be retained in accordance with the
            applicable retention arrangements established for the Customer’s
            Account and as further described in the Privacy Policy.
          </p>
          <p>
            The Customer remains responsible for determining its retention
            requirements and for ensuring that Patient Information is not
            retained for longer than permitted or required by applicable law.
          </p>
          <p>
            Upon the written request of the Customer, or upon termination of the
            Customer’s Account, Patient Information may be deleted in accordance
            with Submitt’s applicable deletion and wind-down procedures, subject
            to any information which Submitt is required by law to retain.
          </p>
          <h5>5.10. Data Subject Rights Preserved</h5>
          <p>
            Nothing in these Terms limits, excludes or otherwise affects any
            right of access, correction, objection or other right conferred on a
            data subject by POPIA, or the manner in which any such right may be
            exercised.
          </p>
          <h5>5.11. No Sale of Patient Information</h5>
          <p>
            Submitt will not sell, rent or otherwise make Patient Information or
            Customer Data available to any third party for that third party’s
            own commercial purposes.
          </p>
          <p>
            Nothing in this clause prevents the transmission of Customer Data to
            a recipient nominated by the Customer, or its processing by a
            service provider engaged by Submitt, in each case in accordance with
            these Terms and the Privacy Policy.
          </p>
          <h5>5.12. Personnel with Access to Patient Information</h5>
          <p>
            Submitt will maintain a record of those of its personnel to whom
            Patient Information is disclosed, and will make that record
            available to the Customer on written request.
          </p>
          <p>
            Each such person is bound by a written undertaking of
            confidentiality in respect of Patient Information.
          </p>
          <h5>5.13. Disclosure Required by Law</h5>
          <p>
            Where Submitt is required by law, court order or a competent
            regulatory authority to disclose Patient Information, Submitt will,
            unless prohibited from doing so, give the Customer as much notice of
            the intended disclosure as is reasonably practicable and lawfully
            permitted, and will limit the disclosure to that which is legally
            required.
          </p>
        </Block>

        <Block>
          <h4>6. MEDICAL BUREAUX AND THIRD-PARTY SERVICES</h4>
          <h5>6.1. Medical Bureaux</h5>
          <p>
            The Customer may use the Application to facilitate the preparation
            and transmission of claims and claims-related information to a
            medical billing bureau selected or authorised by the Customer.
          </p>
          <p>
            The Customer remains solely responsible for its relationship with
            any medical billing bureau and for ensuring that the relevant bureau
            is appropriately authorised to receive, process and submit the
            Customer’s claims and Patient Information.
          </p>
          <p>
            Submitt does not act as the Customer’s medical billing bureau and
            does not assume responsibility for the services, conduct or
            omissions of any medical billing bureau selected by the Customer.
          </p>
          <h5>6.2. Transmission to Medical Bureaux</h5>
          <p>
            The Customer authorises Submitt to transmit Customer Data, including
            Patient Information and claims-related information, to the medical
            billing bureau or other recipient designated by the Customer through
            the functionality made available by the Application.
          </p>
          <p>
            The Customer is responsible for ensuring that the information
            transmitted is accurate, complete and lawfully capable of being
            disclosed to the relevant recipient.
          </p>
          <p>
            Once information has been transmitted to a medical billing bureau or
            other third party at the Customer’s instruction, that recipient’s
            subsequent processing of the information is subject to the terms,
            policies and legal obligations applicable to that recipient.
          </p>
          <h5>6.3. Third-Party Services</h5>
          <p>
            The Application may rely on or integrate with third-party services,
            systems and infrastructure in order to provide particular
            functionality.
          </p>
          <p>Such third-party services may include:</p>
          <p>
            6.3.1. Amazon Web Services (“AWS”), including hosting and
            infrastructure services;
          </p>
          <p>
            6.3.2. Amazon Textract, which is used to assist with the extraction
            of specified information from images submitted through the
            Application;
          </p>
          <p>6.3.3. GoodX, where the applicable integration is enabled; and</p>
          <p>
            6.3.4. other service providers, platforms, APIs or infrastructure
            providers used by Submitt from time to time.
          </p>
          <h5>6.4. Third-Party Availability</h5>
          <p>
            Submitt does not control third-party services and cannot guarantee
            their availability, performance, security, compatibility or
            continued provision.
          </p>
          <p>
            A failure, interruption, modification, suspension or discontinuation
            of a third-party service may affect the availability or
            functionality of corresponding features within the Application.
          </p>
          <p>
            To the extent reasonably practicable, Submitt will take reasonable
            steps to address material disruptions affecting the Services, but
            will not be responsible for failures attributable to a third-party
            provider to the extent that such failure is outside Submitt’s
            reasonable control.
          </p>
          <h5>6.5. Third-Party Terms</h5>
          <p>
            The Customer acknowledges that its use of certain third-party
            services may be subject to additional terms, conditions or policies
            imposed by the relevant third-party provider. The Customer is
            responsible for complying with any such terms to the extent that
            they apply directly to the Customer’s use of the relevant
            third-party service.
          </p>
          <h5>6.6. Third-Party Processing of Personal Information</h5>
          <p>
            Where a third-party service provider processes Patient Information
            or other Personal Information in connection with the Services, such
            processing will be subject to the applicable arrangements described
            in the Privacy Policy and the requirements of applicable data
            protection legislation.
          </p>
          <p>
            The Customer acknowledges and authorises such processing to the
            extent reasonably necessary for the provision of the Services.
          </p>
          <h5>6.7. Bureau Authority and Warranty</h5>
          <p>
            6.7.1. Where a medical billing bureau or any other third party
            requests the creation, administration, suspension, reinstatement or
            cancellation of an Account on behalf of a medical practice, that
            bureau or third party warrants that it holds the practice’s
            authority to give the instruction concerned.
          </p>
          <p>
            6.7.2. Submitt is entitled to accept and act upon such an
            instruction in reliance on that warranty, without further enquiry,
            and the bureau or third party indemnifies Submitt against any claim
            arising from an absence of the authority warranted. Before acting on
            an instruction to cancel, suspend or delete an Account or Customer
            Data, however, Submitt will take reasonable steps to confirm the
            instruction with the Customer’s registered contact. Where that
            contact does not respond within a reasonable period, Submitt may act
            on the instruction in reliance on the warranty in clause 6.7.1.
          </p>
          <p>
            6.7.3. Payment of a subscription fee by a medical billing bureau or
            other third party does not make that party the account holder, and
            transfers to it no right, title or interest in the Account, the
            Customer Data or the Patient Information processed through the
            Account.
          </p>
          <p>
            6.7.4. Submitt may require a medical billing bureau or other third
            party to conclude a Bureau Access Agreement with Submitt, in the
            form prescribed by Submitt from time to time. Where Submitt has done
            so, that party must conclude the agreement within the period
            reasonably specified by Submitt, and until it has done so Submitt
            may decline to act on any instruction from that party or suspend the
            access of its personnel to the Application.
          </p>
        </Block>

        <Block>
          <h4>7. SUBSCRIPTION AND FEES</h4>
          <h5>7.1. Subscription</h5>
          <p>
            Access to the Application is provided on a subscription basis. The
            applicable subscription fee is the fee displayed or otherwise
            communicated to the Customer at the time of subscription.
          </p>
          <p>
            Unless otherwise agreed in writing, the current subscription fee is
            R199.00 per practice per month. For this purpose a practice means a
            registered practice number, and a separate subscription fee is
            payable in respect of each registered practice number.
          </p>
          <h5>7.2. Recurring Subscription</h5>
          <p>
            The Customer’s subscription will continue on a month-to-month basis
            unless cancelled in accordance with these Terms.
          </p>
          <p>
            Subscription fees are payable monthly in advance through the payment
            method made available by Submitt.
          </p>
          <h5>7.3. No Fixed-Term Commitment</h5>
          <p>
            The subscription does not constitute a fixed-term agreement. The
            Customer may cancel its subscription at any time, subject to payment
            of any subscription fees or other amounts accrued and properly due
            up to the effective date of cancellation.
          </p>
          <h5>7.4. Changes to Subscription Fees</h5>
          <p>
            Submitt reserves the right to amend its subscription fees from time
            to time.
          </p>
          <p>
            Where Submitt increases the applicable subscription fee, the revised
            fee will apply from the date communicated to the Customer and, where
            applicable, will be reflected before the Customer’s next billing
            cycle.
          </p>
          <h5>7.5. Failed or Overdue Payments</h5>
          <p>
            If a subscription payment is unsuccessful or remains unpaid, Submitt
            may notify the Customer and provide a reasonable opportunity to
            remedy the non-payment.
          </p>
          <p>
            Submitt may suspend access to the Application if an amount remains
            unpaid 14 (fourteen) days after such notice, and may terminate the
            Customer’s subscription if the amount remains unpaid 60 (sixty) days
            after the first failed or missed payment, in each case at Submitt’s
            election and without prejudice to any other rights Submitt may have.
          </p>
          <h5>7.6. Taxes and Charges</h5>
          <p>
            The Customer is responsible for any taxes, duties, bank charges or
            other amounts imposed on the Customer in connection with its
            subscription, except for taxes imposed on Submitt’s income.
          </p>
          <h5>7.7. Refunds</h5>
          <p>
            Unless otherwise required by law or expressly agreed by Submitt in
            writing, subscription fees paid for a billing period are
            non-refundable once that billing period has commenced.
          </p>
        </Block>

        <Block>
          <h4>8. INTELLECTUAL PROPERTY</h4>
          <h5>8.1. Submitt Intellectual Property</h5>
          <p>
            All right, title and interest in and to the Application and the
            Services, including all intellectual property rights in the
            software, source code, object code, architecture, functionality,
            user interface, designs, databases, workflows, processes,
            documentation, trade marks, logos and other materials comprising or
            relating to the application, are and remain the property of Submitt
            Medical Accounts (Pty) Ltd or its licensors.
          </p>
          <p>
            Except for the limited right to use the Application expressly
            granted under these Terms, nothing in these Terms transfers, assigns
            or grants to the Customer any ownership interest in or to Submitt’s
            intellectual property.
          </p>
          <h5>8.2. Licence to Use the Application</h5>
          <p>
            Subject to the Customer’s continued compliance with these Terms and
            payment of the applicable subscription fees, Submitt grants the
            Customer a limited, non-exclusive, non-transferable and revocable
            right to access and use the Application during the applicable
            subscription period for the Customer’s internal business purposes.
          </p>
          <p>
            The Customer may permit its Authorised Users to exercise this right
            on its behalf, provided that the Customer remains responsible for
            their use of the Application.
          </p>
          <h5>8.3. Customer Data</h5>
          <p>
            The Customer retains all right, title and interest that it may have
            in and to Customer Data.
          </p>
          <p>
            Nothing in these Terms transfers ownership of Customer Data or
            Patient Information to Submitt.
          </p>
          <p>
            The Customer grants Submitt a limited, non-exclusive right and
            licence to host, store, reproduce, transmit and otherwise process
            Customer Data to the extent reasonably necessary to provide,
            maintain, secure, support and improve the Services and to perform
            Submitt’s obligations under these Terms.
          </p>
          <h5>8.4. Feedback</h5>
          <p>
            If the Customer or an Authorised User provides Submitt with
            suggestions, recommendations, comments or other feedback concerning
            the Application or Services, Submitt may use and incorporate such
            feedback into its products and services without restriction or
            obligation to the Customer, provided that Submitt does not disclose
            the Customer’s Confidential Information or Personal Information in
            doing so.
          </p>
          <h5>8.5. Restrictions</h5>
          <p>
            Except to the extent expressly permitted by these Terms or
            applicable law, the Customer must not:
          </p>
          <p>
            8.5.1. copy, reproduce, modify, adapt or create derivative works of
            the Application;
          </p>
          <p>
            8.5.2. reverse engineer, decompile, disassemble or otherwise attempt
            to derive the source code or underlying structure of the
            Application;
          </p>
          <p>
            8.5.3. reproduce or commercially exploit any part of the Application
            or Services other than through the permitted use contemplated by
            these Terms;
          </p>
          <p>
            8.5.4. remove, obscure or alter any copyright, trade mark or other
            proprietary notices contained in or relating to the Application;
          </p>
          <p>
            8.5.5. use the Application to develop, train, test or facilitate the
            development of a competing product or service;
          </p>
          <p>
            8.5.6. access or use the Application for the purpose of copying or
            replicating its functionality, design or user interface; or
          </p>
          <p>
            8.5.7. permit any unauthorised third party to access or use the
            Application.
          </p>
          <p>Breach of this clause 8.5 attracts the penalty in clause 8.7.</p>
          <h5>8.6. Third-Party Intellectual Property</h5>
          <p>
            The Application may incorporate or interact with software,
            technology, services or other materials owned by third parties.
          </p>
          <p>
            The rights in such third-party materials remain vested in their
            respective owners and are subject to any applicable third-party
            licence terms.
          </p>
          <p>
            The Customer acquires no ownership rights in such third-party
            intellectual property by virtue of using the Application.
          </p>
          <h5>8.7. Penalty</h5>
          <p>
            8.7.1. The Customer acknowledges that a breach of clause 8.5 is
            likely to cause Submitt harm which is difficult to quantify, and
            that the penalty in clause 8.7.2 is a genuine pre-estimate of that
            harm and is reasonable in the circumstances.
          </p>
          <p>
            8.7.2. As a penalty stipulation in terms of the Conventional
            Penalties Act 15 of 1962, the Customer shall on written demand pay
            Submitt an amount of R250,000.00 (two hundred and fifty thousand
            rand) in respect of each separate breach of clause 8.5, and where a
            breach is of a continuing nature, a further amount of R25,000.00
            (twenty-five thousand rand) in respect of each month or part of a
            month during which the breach continues.
          </p>
          <p>
            8.7.3. In accordance with section 2(1) of the Conventional Penalties
            Act 15 of 1962, Submitt may recover either the penalty or damages in
            respect of the same breach, but not both, and shall elect between
            them.
          </p>
          <p>
            8.7.4. Payment of the penalty does not relieve the Customer of its
            obligation to cease the conduct constituting the breach, and does
            not affect Submitt’s right to obtain interdictory relief, to
            terminate the subscription under clause 14, or to pursue any other
            remedy available to it in law.
          </p>
          <p>
            8.7.5. This clause survives termination of the Customer’s
            subscription.
          </p>
        </Block>

        <Block>
          <h4>9. CONFIDENTIALITY</h4>
          <p>
            Each party must keep confidential information received from the
            other party confidential and may use it only for purposes connected
            with the relationship between the parties.
          </p>
          <p>
            The Customer must ensure that its Authorised Users, employees and
            contractors who have access to confidential information are subject
            to appropriate confidentiality obligations.
          </p>
          <p>
            Confidentiality obligations relating to Patient Information and
            Personal Information continue after termination of the Customer’s
            subscription for so long as the information remains confidential or
            protected by law.
          </p>
        </Block>

        <Block>
          <h4>10. DATA SECURITY AND RETENTION</h4>
          <h5>10.1. Security Measures</h5>
          <p>
            Submitt will implement and maintain reasonable technical and
            organisational measures appropriate to the nature of the Customer
            Data processed through the Application, including measures intended
            to protect Customer Data and Patient Information against
            unauthorised access, loss, destruction, damage, alteration or
            disclosure.
          </p>
          <p>
            The Customer acknowledges that no electronic system, network or
            method of transmission can be guaranteed to be completely secure.
          </p>
          <h5>10.2. Customer Security Responsibilities</h5>
          <p>
            The Customer remains responsible for the security of its own
            devices, systems, networks and internet connections used to access
            the Application, as well as the security of its Account and user
            credentials.
          </p>
          <p>
            The Customer must take reasonable measures to prevent unauthorised
            persons from accessing the Application or Customer Data through the
            Customer’s systems or Accounts.
          </p>
          <h5>10.3. Cloud Storage and Third-Party Infrastructure</h5>
          <p>
            Customer Data may be stored and processed using cloud-based
            infrastructure and third-party technology providers engaged by
            Submitt in connection with the Services. The Customer acknowledges
            that such infrastructure may include AWS and that certain processing
            may take place outside the Republic of South Africa, as more fully
            described in the Privacy Policy.
          </p>
          <h5>10.4. AI Services</h5>
          <p>
            The Customer acknowledges that certain images and information
            submitted through the Application may be processed using Amazon
            Textract for the purpose of extracting information required for the
            functionality of the Application.
          </p>
          <p>
            The use of Amazon Textract and the applicable arrangements relating
            to the processing of information through AWS are further described
            in the Privacy Policy.
          </p>
          <h5>10.5. Retention of Customer Data</h5>
          <p>
            Customer Data, including uploaded documents, is retained for the
            duration of the Customer’s Account, on the standing instruction of
            the Customer. Submitt does not apply an automatic deletion period to
            Customer Data held on an active Account.
          </p>
          <p>
            The Customer remains responsible for determining the retention
            period appropriate to its own legal, regulatory, professional and
            business requirements.
          </p>
          <h5>10.6. Deletion of Customer Data</h5>
          <p>
            Customer Data is deleted on the written request of the Customer, or
            following termination of the Account on expiry of the wind-down
            period referred to in clause 10.9.
          </p>
          <p>
            Submitt’s internal processing artefacts, including system logs and
            temporary files, are retained only for a short operational period,
            as described in the Privacy Policy.
          </p>
          <h5>10.7. Backups and Recovery</h5>
          <p>
            Submitt may maintain backup and versioned copies of Customer Data
            for purposes of system recovery, security and business continuity.
          </p>
          <p>
            The existence of backups does not constitute an independent right to
            indefinite retention of Customer Data.
          </p>
          <p>
            Where Customer Data is deleted from the active Application in
            accordance with clause 10.6, corresponding backup or previous
            versions may remain temporarily available for recovery in accordance
            with Submitt’s backup and deletion procedures before being
            permanently purged.
          </p>
          <h5>10.8. Suspension</h5>
          <p>
            Suspension of an Account does not, by itself, result in the deletion
            of Customer Data. Customer Data associated with a suspended Account
            will remain subject to the applicable retention arrangements unless
            otherwise required by law or agreed between the parties.
          </p>
          <h5>10.9. Termination and Wind-Down</h5>
          <p>
            Following termination of the Customer’s Account, Submitt may retain
            Customer Data for a wind-down period of 30 (thirty) days to allow
            the Customer to request an export of its Customer Data.
          </p>
          <p>
            Following expiry of the applicable wind-down and retention period,
            Customer Data may be permanently deleted in accordance with
            Submitt’s applicable deletion procedures, subject to any information
            which Submitt is required by law to retain.
          </p>
          <h5>10.10. Legal Retention</h5>
          <p>
            Nothing in these Terms requires Submitt to delete or otherwise
            dispose of information which it is required to retain by applicable
            law, a court or competent regulatory authority.
          </p>
          <p>
            Any information retained pursuant to such obligation will remain
            subject to applicable confidentiality and data-protection
            obligations.
          </p>
        </Block>

        <Block>
          <h4>11. CUSTOMER DATA EXPORT AND TERMINATION</h4>
          <h5>11.1. Customer’s Right to Export</h5>
          <p>
            Upon termination or cancellation of the Customer’s subscription, the
            Customer may request an export of its Customer Data from Submitt
            within the applicable wind-down period.
          </p>
          <p>
            The export will, subject to the functionality and format made
            available by Submitt, comprise:
          </p>
          <p>11.1.1. a folder containing the Customer’s uploaded images; and</p>
          <p>
            11.1.2. a spreadsheet containing the Customer’s claims, including
            the patient details captured in connection with those claims,
            applicable codes, dates and claim references.
          </p>
          <h5>11.2. Excluded Information</h5>
          <p>
            The Customer Data export will not include Submitt’s internal system
            information, including AI processing data, system logs, internal
            metadata, proprietary system information or other information which
            does not constitute Customer Data.
          </p>
          <h5>11.3. Export Process</h5>
          <p>
            Data exports are not available through a self-service export
            function. An export must be requested by the Customer and will be
            manually prepared and made available by Submitt.
          </p>
          <p>
            The Customer acknowledges that the preparation of an export may
            require reasonable processing time.
          </p>
          <h5>11.4. Export Fee</h5>
          <p>
            A Customer Data export is a chargeable service, charged at Submitt’s
            then-current published export rates. Submitt may invoice the
            Customer for the applicable export fee before preparing or releasing
            the export.
          </p>
          <h5>11.5. Termination and Wind-Down</h5>
          <p>
            Following cancellation or termination of the Customer’s
            subscription, Submitt may maintain the Customer’s Account and
            Customer Data for a wind-down period of 30 (thirty) days to allow
            the Customer to request an export.
          </p>
          <p>
            The Customer remains responsible for any amounts properly due to
            Submitt during the applicable wind-down period.
          </p>
          <h5>11.6. Deletion Following Termination</h5>
          <p>
            Following expiry of the applicable wind-down period, and subject to
            any applicable retention period or legal obligation, Submitt may
            delete the Customer’s Customer Data in accordance with its data
            retention and deletion procedures.
          </p>
          <p>
            Deletion may include Customer Data held in the active Application
            and, following the applicable backup retention period, corresponding
            backup or previous versions.
          </p>
          <h5>11.7. No Automatic Deletion on Suspension</h5>
          <p>
            Suspension of the Customer’s Account does not constitute termination
            and will not, by itself, result in deletion of Customer Data.
          </p>
          <h5>11.8. Customer’s Responsibility Following Export</h5>
          <p>
            The Customer remains responsible for ensuring that any Customer Data
            exported to it is stored, secured, retained and ultimately destroyed
            in accordance with its own legal, regulatory and professional
            obligations, including its obligations under POPIA.
          </p>
          <h5>11.9. Practice Closure, Retirement or Death of a Practitioner</h5>
          <p>
            11.9.1. The Customer acknowledges that its statutory, regulatory and
            professional record-keeping obligations survive the closure of its
            practice, the retirement or death of a practitioner, and the
            termination of its subscription, and that those obligations pass to
            the successor practice, the incorporated entity, or the executor of
            the deceased estate, as the case may be.
          </p>
          <p>
            11.9.2. Submitt may accept instructions in respect of an Account,
            including a request for an export of Customer Data at Submitt’s
            then-current rates, from an executor, curator, trustee or authorised
            successor of the Customer, on receipt of reasonable proof of that
            person’s authority.
          </p>
          <p>
            11.9.3. Where an Account becomes orphaned, meaning that subscription
            payments cease and Submitt’s reasonable attempts to contact the
            persons listed as the Account’s contacts are unsuccessful, the
            suspension, termination, export-offer and deletion provisions of
            clauses 10, 11 and 14 apply in the ordinary course.
          </p>
          <p>
            11.9.4. The Customer, its successor practice or its estate remains
            at all times the legal custodian of the records of the practice.
            Submitt provides a storage and processing service only and does not
            at any time become the custodian or successor custodian of any
            patient record.
          </p>
        </Block>

        <Block>
          <h4>12. DISCLAIMER OF WARRANTIES</h4>
          <h5>12.1. Provision of the Services</h5>
          <p>
            The Application and Services are provided on an “as available”
            basis. While Submitt will use reasonable endeavours to maintain the
            functionality and availability of the Application, Submitt does not
            warrant that the Application or Services will be uninterrupted,
            secure, error-free or available at all times.
          </p>
          <h5>12.2. Accuracy of Information</h5>
          <p>
            Submitt does not warrant that any information processed, generated,
            extracted or displayed through the Application will be accurate,
            complete or error-free.
          </p>
          <p>
            In particular, the Customer acknowledges that information extracted
            from uploaded images through the Application’s AI-assisted
            functionality may contain errors or omissions and must be
            independently verified by the Customer before being relied upon.
          </p>
          <h5>12.3. Billing and Claims</h5>
          <p>
            The Application is intended to assist with the administrative
            preparation and management of medical claims and does not constitute
            professional medical, clinical, billing, accounting or legal advice.
          </p>
          <p>Submitt does not warrant or guarantee that:</p>
          <p>
            12.3.1. any particular billing code, ICD-10 code, material code,
            modifier or macro code is appropriate in the circumstances;
          </p>
          <p>
            12.3.2. any claim prepared or processed through the Application is
            accurate, complete or professionally appropriate;
          </p>
          <p>
            12.3.3. any claim will be accepted, processed or paid by a medical
            scheme or other third party;
          </p>
          <p>
            12.3.4. any particular amount will be reimbursed or paid in respect
            of a claim; or
          </p>
          <p>
            12.3.5. the Application will identify every error, omission or issue
            in a claim.
          </p>
          <p>
            The Customer remains responsible for reviewing and approving all
            information and claims before they are submitted or transmitted to a
            medical billing bureau, medical scheme or other third party.
          </p>
          <h5>12.4. Third-Party Services</h5>
          <p>
            The Application may depend upon third-party services, systems,
            infrastructure and integrations, including AWS, Amazon Textract,
            GoodX and medical billing bureaux. Submitt does not warrant the
            availability, accuracy, performance, security or continued operation
            of any third-party service and is not responsible for any
            interruption, failure, modification or discontinuation of a
            third-party service to the extent that such event is outside
            Submitt’s reasonable control.
          </p>
          <h5>12.5. Customer Data</h5>
          <p>
            The Customer is responsible for ensuring that Customer Data
            submitted to or processed through the Application is accurate,
            complete, lawfully obtained and appropriate for the purposes for
            which it is processed.
          </p>
          <p>
            Submitt does not warrant that the Application will detect or correct
            inaccuracies, omissions or inconsistencies in Customer Data.
          </p>
          <h5>12.6. No Professional Relationship</h5>
          <p>
            Nothing contained in the Application or Services constitutes
            medical, clinical, legal, accounting or other professional advice,
            and no professional relationship is created between Submitt and any
            patient by virtue of the Customer’s use of the Application.
          </p>
          <p>
            The Customer and its relevant professional users remain solely
            responsible for exercising their own professional judgment.
          </p>
          <h5>12.7. Disclaimer of Implied Warranties</h5>
          <p>
            To the maximum extent permitted by applicable law, all warranties,
            representations, conditions or terms not expressly set out in these
            Terms, whether express, implied or statutory, are excluded.
          </p>
          <p>
            Nothing in these Terms excludes or limits any warranty,
            representation, condition, right or remedy which cannot lawfully be
            excluded or limited.
          </p>
          <h5>12.8. Customer’s Acknowledgement</h5>
          <p>
            The Customer acknowledges that the Application is a technology and
            administrative tool intended to assist the Customer and its
            Authorised Users and is not a substitute for appropriate human
            verification, professional judgement, oversight or decision-making.
          </p>
          <h5>12.9. Code Libraries</h5>
          <p>
            Billing codes, ICD-10 codes, material codes, modifiers and macro
            codes made available through the Application are supplied for
            convenience only. Submitt does not warrant that any such code is
            current, complete, accurate or appropriate for a particular claim,
            patient or procedure.
          </p>
          <p>
            The selection and application of codes and modifiers is the sole
            professional responsibility of the Customer and its Authorised
            Users, and Submitt is not liable for any loss arising from the use
            of an invalid, outdated or inappropriate code, material code or
            modifier.
          </p>
        </Block>

        <Block>
          <h4>13. LIMITATION OF LIABILITY</h4>
          <h5>13.1. Exclusion of Certain Losses</h5>
          <p>
            To the maximum extent permitted by applicable law, Submitt will not
            be liable to the Customer or any third party for any indirect,
            consequential, special or incidental loss or damage arising out of
            or in connection with these Terms or the Customer’s access to or use
            of the Application, including any loss of profits, revenue,
            business, anticipated savings, goodwill, reputation or business
            opportunity.
          </p>
          <h5>13.2. Customer’s Use of the Application</h5>
          <p>Submitt will not be liable for any loss or damage arising from:</p>
          <p>
            13.2.1. the Customer’s failure to verify information processed,
            generated or extracted through the Application;
          </p>
          <p>
            13.2.2. errors, omissions or inaccuracies in Customer Data supplied
            by the Customer or its Authorised Users;
          </p>
          <p>
            13.2.3. any claim, billing information or coding prepared, approved
            or submitted by the Customer or its Authorised Users;
          </p>
          <p>
            13.2.4. the Customer’s failure to obtain any required consent,
            authorisation or lawful basis for processing Patient Information;
          </p>
          <p>
            13.2.5. any unauthorised use of the Application resulting from the
            Customer’s failure to safeguard its Account or login credentials; or
          </p>
          <p>
            13.2.6. any act or omission of the Customer, its Authorised Users,
            medical billing bureau or other third party.
          </p>
          <h5>13.3. Third-Party Services</h5>
          <p>
            Submitt will not be liable for any loss or damage arising from the
            failure, interruption, unavailability, modification or
            discontinuation of any third-party service, system, infrastructure
            or integration used in connection with the Application, including
            AWS, Amazon Textract, GoodX, telecommunications services or a
            medical billing bureau, except to the extent that such loss or
            damage is directly caused by Submitt’s own breach of these Terms.
          </p>
          <h5>13.4. Liability Cap</h5>
          <p>
            Subject to clauses 13.5 and 13.6, Submitt’s total aggregate
            liability arising out of or in connection with these Terms, whether
            in contract, delict or otherwise, will not exceed the total
            subscription fees actually paid by the Customer to Submitt during
            the 12 months immediately preceding the event giving rise to the
            claim.
          </p>
          <h5>13.5. Exclusions Required by Law</h5>
          <p>
            Nothing in these Terms excludes or limits any liability, right or
            remedy to the extent that such exclusion or limitation is prohibited
            by applicable law.
          </p>
          <h5>13.6. Fraud, Wilful Misconduct and Gross Negligence</h5>
          <p>
            Nothing in these Terms will operate to exclude or limit Submitt’s
            liability to the extent arising from fraud, wilful misconduct or
            gross negligence, to the extent that such liability cannot lawfully
            be excluded or limited.
          </p>
          <h5>13.7. Customer’s Responsibility</h5>
          <p>
            The Customer acknowledges that it uses the Application as an
            administrative tool and remains responsible for exercising
            appropriate professional judgement, verifying information and
            reviewing claims before submission.
          </p>
          <p>
            The limitations and exclusions in this clause apply to the maximum
            extent permitted by law and are intended to allocate responsibility
            between the parties having regard to the nature of the Services.
          </p>
          <h5>13.8. Customer Indemnity</h5>
          <p>
            The Customer indemnifies Submitt and holds Submitt harmless against
            any claim, demand, action, proceeding, liability, loss, damage,
            penalty, administrative fine, cost or expense (including legal costs
            on an attorney-and-own-client scale) suffered or incurred by Submitt
            and arising out of or in connection with:
          </p>
          <p>
            13.8.1. any claim, code, modifier or billing information prepared,
            reviewed, approved, transmitted or submitted by the Customer or an
            Authorised User;
          </p>
          <p>
            13.8.2. the Customer’s failure to review or verify information
            extracted through the Application’s AI-assisted functionality before
            relying on it;
          </p>
          <p>
            13.8.3. the Customer’s failure to obtain any consent, authorisation
            or other lawful basis required for the processing of Patient
            Information, or to provide any notice required under POPIA;
          </p>
          <p>
            13.8.4. any access to the Application or to Customer Data granted or
            permitted by the Customer, or which the Customer failed to prevent,
            including access by an Authorised User, a medical billing bureau, or
            any person using the Customer’s Account credentials;
          </p>
          <p>
            13.8.5. any breach by the Customer or an Authorised User of these
            Terms, of any applicable law, or of any professional or ethical rule
            applicable to the Customer; and
          </p>
          <p>
            13.8.6. any claim by a patient, medical scheme, medical billing
            bureau, or any regulatory or professional body arising from the
            Customer’s use of the Application.
          </p>
          <p>
            The limitation of liability in clause 13.4 does not apply to the
            Customer’s obligations under this clause 13.8. This indemnity does
            not extend to loss or damage to the extent directly caused by
            Submitt’s own fraud, wilful misconduct or gross negligence, and
            survives termination of the Customer’s subscription.
          </p>
          <h5>13.9. Consumer Protection Act</h5>
          <p>
            13.9.1. The Customer acknowledges that the Application is supplied
            for use in the Customer’s practice or business and not for private
            or domestic purposes.
          </p>
          <p>
            13.9.2. The Consumer Protection Act 68 of 2008 (“CPA”) does not
            apply to a transaction in terms of which the Customer is a juristic
            person whose asset value or annual turnover, at the time of the
            transaction, equals or exceeds the threshold determined by the
            Minister in terms of section 6 of the CPA.
          </p>
          <p>
            13.9.3. Where the CPA applies to the Customer, nothing in these
            Terms is intended to, or does, exclude, limit or waive any right or
            remedy conferred on the Customer by the CPA which cannot lawfully be
            excluded, limited or waived, and these Terms must be read as subject
            to those rights.
          </p>
          <p>
            <strong>
              13.9.4. The Customer’s attention is specifically drawn, in terms
              of section 49 of the CPA, to those provisions of these Terms which
              limit or exclude Submitt’s liability, which require the Customer
              to assume a risk, or which impose an obligation on the Customer to
              indemnify Submitt, being clauses 3.6, 12, 13.1 to 13.4 and 13.8.
              The Customer acknowledges that it has been given an adequate
              opportunity to read and consider those provisions and that it
              understands their effect.
            </strong>
          </p>
        </Block>

        <Block>
          <h4>14. SUSPENSION AND TERMINATION BY SUBMITT</h4>
          <h5>14.1. Suspension of Access</h5>
          <p>
            Submitt may suspend or restrict the Customer’s or an Authorised
            User’s access to the Application where reasonably necessary:
          </p>
          <p>
            14.1.1. to protect the security, integrity or availability of the
            Application or any underlying system;
          </p>
          <p>
            14.1.2. to prevent unauthorised access to or use of the Application;
          </p>
          <p>
            14.1.3. where the Customer or an Authorised User has materially
            breached these Terms;
          </p>
          <p>
            14.1.4. where the Customer has failed to pay any amount properly due
            to Submitt after receiving notice of the outstanding amount, in
            accordance with clause 7.5;
          </p>
          <p>
            14.1.5. where the Customer or an Authorised User uses the
            Application in an unlawful, fraudulent or materially abusive manner;
          </p>
          <p>
            14.1.6. where continued access to the Application presents a
            material risk to Submitt, another customer, a data subject or any
            third party; or
          </p>
          <p>
            14.1.7. where suspension is required by law, a court or other
            competent authority.
          </p>
          <h5>14.2. Notice of Suspension</h5>
          <p>
            Where reasonably practicable, Submitt will notify the Customer
            before suspending access and will, where appropriate, provide the
            Customer with a reasonable opportunity to remedy the circumstances
            giving rise to the suspension.
          </p>
          <p>
            Submitt may, however, suspend access immediately where reasonably
            necessary to address a security incident, prevent unlawful or
            unauthorised access, protect Personal Information or comply with a
            legal or regulatory requirement.
          </p>
          <h5>14.3. Effect of Suspension</h5>
          <p>
            Suspension of an Account does not constitute termination of the
            Customer’s subscription unless Submitt expressly terminates the
            subscription in accordance with these Terms.
          </p>
          <p>
            Suspension will not, by itself, result in the deletion of Customer
            Data. The Customer remains responsible for all amounts properly due
            during any period of suspension, except to the extent that the
            suspension results from Submitt’s own material breach of these
            Terms.
          </p>
          <h5>14.4. Termination for Material Breach</h5>
          <p>
            Submitt may terminate the Customer’s subscription if the Customer
            materially breaches these Terms and fails to remedy the breach
            within a reasonable period after receiving written notice requiring
            it to do so.
          </p>
          <h5>14.5. Immediate Termination</h5>
          <p>
            Submitt may terminate the Customer’s subscription with immediate
            effect where:
          </p>
          <p>14.5.1. continued provision of the Services would be unlawful;</p>
          <p>
            14.5.2. the Customer engages in fraudulent or materially unlawful
            conduct through the Application;
          </p>
          <p>
            14.5.3. the Customer’s use of the Application creates a material and
            immediate security or privacy risk which cannot reasonably be
            addressed through suspension or other measures; or
          </p>
          <p>
            14.5.4. Submitt is required to terminate the Services by law, a
            court or other competent authority.
          </p>
          <h5>14.6. Termination for Operational Reasons</h5>
          <p>
            Submitt may discontinue the Application or materially discontinue
            the Services by providing reasonable notice to affected Customers,
            where reasonably practicable.
          </p>
          <p>
            Where Submitt permanently discontinues the Services, Submitt will
            make reasonable provision for the Customer to obtain an export of
            its Customer Data in accordance with clause 11.
          </p>
          <h5>14.7. Customer Cancellation</h5>
          <p>
            Nothing in this clause limits the Customer’s right to cancel its
            subscription at any time in accordance with clause 7.
          </p>
          <h5>14.8. Consequences of Termination</h5>
          <p>Upon termination of the Customer’s subscription:</p>
          <p>
            14.8.1. the Customer’s right to access and use the Application will
            cease;
          </p>
          <p>
            14.8.2. the Customer must cease permitting its Authorised Users to
            access the Application;
          </p>
          <p>
            14.8.3. the Customer may request an export of its Customer Data in
            accordance with clause 11; and
          </p>
          <p>
            14.8.4. Customer Data may thereafter be retained and deleted in
            accordance with clause 10 and clause 11 and the Privacy Policy.
          </p>
          <p>
            Termination will not affect any rights or obligations which accrued
            before the effective date of termination.
          </p>
        </Block>

        <Block>
          <h4>15. CHANGES TO THESE TERMS</h4>
          <p>
            Submitt may amend these Terms from time to time to reflect changes
            to the Services, applicable law, technology, security requirements
            or Submitt’s business arrangements.
          </p>
          <p>
            Where a revised version is issued, the Customer may be required to
            accept the revised Terms before continuing to use the Application.
          </p>
          <p>
            The version of the Terms accepted by the Customer, together with the
            date and time of acceptance, will be recorded against the Customer’s
            Account.
          </p>
          <p>
            Where a material amendment requires affirmative acceptance, failure
            to accept the revised Terms may result in suspension of access until
            acceptance has been recorded.
          </p>
        </Block>

        <Block>
          <h4>16. BREACH</h4>
          <p>
            If either party materially breaches these Terms and fails to remedy
            the breach within a reasonable period after receiving written notice
            requiring it to do so, the innocent party may exercise any rights
            and remedies available to it in terms of these Terms and applicable
            law.
          </p>
          <p>
            Nothing prevents a party from taking immediate action where
            reasonably necessary to protect Personal Information, confidential
            information, the security of its systems or the rights of third
            parties.
          </p>
        </Block>

        <Block>
          <h4>17. FORCE MAJEURE</h4>
          <p>
            Neither party will be liable for any delay or failure to perform an
            obligation caused by circumstances beyond its reasonable control,
            including telecommunications or internet failures, hosting failures,
            interruptions to third-party services, electricity failures, natural
            disasters, civil unrest, strikes, governmental action or other
            events beyond the affected party’s reasonable control.
          </p>
        </Block>

        <Block>
          <h4>18. GENERAL</h4>
          <h5>18.1. Entire agreement</h5>
          <p>
            These Terms and the Privacy Policy constitute the agreement between
            Submitt and the Customer concerning the Customer’s use of the
            Application, together with any specific written terms expressly
            agreed between the parties.
          </p>
          <h5>18.2. Severability</h5>
          <p>
            If any provision is held to be invalid or unenforceable, that
            provision will be severed or limited to the extent necessary and the
            remaining provisions will continue in force.
          </p>
          <h5>18.3. No waiver</h5>
          <p>
            A failure or delay by either party to exercise a right does not
            constitute a waiver of that right.
          </p>
          <h5>18.4. Assignment</h5>
          <p>
            The Customer may not assign or transfer its rights or obligations
            under these Terms without Submitt’s prior written consent.
          </p>
          <p>
            Submitt may assign these Terms as part of a sale, restructuring,
            merger or transfer of its business or the relevant Services.
          </p>
          <h5>18.5. Independent parties</h5>
          <p>
            Nothing in these Terms creates a partnership, joint venture,
            employment, agency or fiduciary relationship between the parties.
          </p>
          <h5>18.6. Governing law</h5>
          <p>
            These Terms are governed by the laws of the Republic of South
            Africa.
          </p>
          <h5>18.7. Jurisdiction</h5>
          <p>
            The parties submit to the jurisdiction of the South African courts
            having jurisdiction in respect of any dispute arising from or
            relating to these Terms.
          </p>
          <h5>18.8. Attribution</h5>
          <p>
            Submitt.App is a product of Submitt Medical Accounts (Pty) Ltd, Reg.
            No. 2013/008088/07.
          </p>
        </Block>

        <Block>
          <h4>19. CONTACT DETAILS</h4>
          <p>
            Submitt Medical Accounts (Pty) Ltd
            <br />
            Registration Number: 2013/008088/07
            <br />
            Address: Mooikloof Office Park, West Block, Block 7, Cnr Atterbury
            Road and Jollify Main Road, Mooikloof, Pretoria, 0081.
            <br />
            Email: info@submitt.app
            <br />
            Telephone: 012-3441370
          </p>
        </Block>

        <Block>
          <h4>ACCEPTANCE</h4>
          <p>
            By clicking “I Accept”, “I Agree” or any equivalent electronic
            acceptance mechanism, the Customer acknowledges that such electronic
            acceptance constitutes a legally binding expression of its intention
            to be bound by these Terms and constitutes an electronic signature
            for purposes of the Electronic Communications and Transactions Act
            25 of 2002 (“ECTA”), with the same legal effect as a written
            signature to the extent permitted by applicable law.
          </p>
          <p>
            By clicking “I Agree”, creating an Account or accessing or using
            Submitt.App, you confirm that:
          </p>
          <p>1. you have read and understood these Terms;</p>
          <p>2. you agree to be legally bound by them;</p>
          <p>
            3. you are authorised to accept them on behalf of the Customer,
            where applicable; and
          </p>
          <p>
            4. you have read the{" "}
            <Link to="/privacy/">Submitt POPIA Privacy Policy</Link>.
          </p>
        </Block>
      </Container>
    </Layout>
  )
}

export default Terms
